import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldX, ArrowLeft } from 'lucide-react';

// Rutas permitidas para el rol "aliado"
const ALIADO_ALLOWED_PATHS = [
  '/panel-aliado',
  '/aliados',
  '/cuentas-cobro',
  '/profile'
];

// Rutas permitidas para el rol "vendedor"
const VENDEDOR_ALLOWED_PATHS = [
  '/dashboard',
  '/quotes',
  '/clientes',
  '/sheet',
  '/profile'
];

// Rutas permitidas para el rol "diseñador" y "asesor_diseñador"
const DISENADOR_ALLOWED_PATHS = [
  '/dashboard',
  '/quotes',
  '/clientes',
  '/sheet',
  '/profile'
];

// Rutas permitidas para el rol "comercial"
const COMERCIAL_ALLOWED_PATHS = [
  '/dashboard',
  '/quotes',
  '/aliados',
  '/referidos',
  '/profile'
];

// Rutas permitidas para el rol "contabilidad"
const CONTABILIDAD_ALLOWED_PATHS = [
  '/dashboard',
  '/aliados',
  '/referidos',
  '/descargas',
  '/cuentas-cobro',
  '/profile'
];

// Verificar si una ruta está permitida para aliados
const isAliadoAllowedPath = (pathname, user) => {
  if (ALIADO_ALLOWED_PATHS.includes(pathname)) return true;
  
  // Bloquear explícitamente la creación de nuevos aliados
  if (pathname === '/aliados/nuevo') return false;
  
  // Permitir rutas de su propio perfil de aliado
  // El aliado solo puede acceder a su propia información
  // Las validaciones específicas se hacen en el backend
  if (pathname.startsWith('/aliados/') && pathname !== '/aliados/nuevo') {
    // Rutas permitidas: /aliados/{id}, /aliados/{id}/editar, /aliados/{id}/referidos
    return true;
  }
  
  return false;
};

// Verificar si una ruta está permitida para vendedor
const isVendedorAllowedPath = (pathname) => {
  if (pathname === '/admin/bodas') return true;
  if (VENDEDOR_ALLOWED_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/quotes')) return true;
  if (pathname.startsWith('/clientes')) return true;
  if (pathname.startsWith('/sheet/')) return true;
  return false;
};

// Verificar si una ruta está permitida para diseñador
const isDisenadorAllowedPath = (pathname) => {
  if (pathname === '/admin/bodas') return true;
  if (DISENADOR_ALLOWED_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/quotes')) return true;
  if (pathname.startsWith('/clientes')) return true;
  if (pathname.startsWith('/sheet/')) return true;
  return false;
};

// Verificar si una ruta está permitida para comercial
const isComercialAllowedPath = (pathname) => {
  if (COMERCIAL_ALLOWED_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/quotes')) return true;
  if (pathname.startsWith('/aliados/')) return true;
  if (pathname.startsWith('/sheet/')) return true;
  return false;
};

// Verificar si una ruta está permitida para contabilidad
const isContabilidadAllowedPath = (pathname) => {
  if (CONTABILIDAD_ALLOWED_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/aliados/')) return true;
  if (pathname.startsWith('/cuentas-cobro')) return true;
  return false;
};

// Componente de acceso denegado
const AccessDenied = ({ userRole, redirectPath }) => {
  const getRoleName = (role) => {
    const names = {
      'aliado': 'Aliado',
      'comercial': 'Comercial',
      'contabilidad': 'Contabilidad',
      'gerencia': 'Gerencia',
      'admin': 'Administrador',
      'vendedor': 'Vendedor',
      'diseñador': 'Diseñador',
      'asesor_diseñador': 'Asesor Diseñador',
      'asesor': 'Asesor'
    };
    return names[role] || role;
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
          <div className="w-20 h-20 mx-auto bg-red-500/10 rounded-full flex items-center justify-center mb-6">
            <ShieldX className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="font-display text-2xl text-white mb-3">Acceso Denegado</h1>
          <p className="text-zinc-400 mb-6">
            No tienes permisos para acceder a este módulo.
            <br />
            <span className="text-zinc-500 text-sm">
              Tu rol actual es: <strong className="text-[#D4AF37]">{getRoleName(userRole)}</strong>
            </span>
          </p>
          <a
            href={redirectPath}
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 rounded-lg hover:bg-[#C49F27] transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Ir a mi panel
          </a>
        </div>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm uppercase tracking-widest text-zinc-400">Cargando...</p>
        </div>
      </div>
    );
  }

  // Si no hay usuario autenticado, redirigir a login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = user.role;
  const pathname = location.pathname;

  // Si el usuario es aliado
  if (userRole === 'aliado') {
    // Redirigir desde el dashboard o raíz al panel del aliado
    if (pathname === '/dashboard' || pathname === '/') {
      return <Navigate to="/panel-aliado" replace />;
    }
    // Bloquear acceso a rutas no permitidas
    if (!isAliadoAllowedPath(pathname, user)) {
      return <AccessDenied userRole={userRole} redirectPath="/panel-aliado" />;
    }
  }

  // Si el usuario es comercial
  if (userRole === 'comercial') {
    if (pathname === '/' ) {
      return <Navigate to="/dashboard" replace />;
    }
    if (!isComercialAllowedPath(pathname)) {
      return <AccessDenied userRole={userRole} redirectPath="/dashboard" />;
    }
  }

  // Si el usuario es contabilidad
  if (userRole === 'contabilidad') {
    if (pathname === '/') {
      return <Navigate to="/dashboard" replace />;
    }
    if (!isContabilidadAllowedPath(pathname)) {
      return <AccessDenied userRole={userRole} redirectPath="/dashboard" />;
    }
  }

  // Si el usuario es vendedor
  if (userRole === 'vendedor') {
    if (pathname === '/') {
      return <Navigate to="/dashboard" replace />;
    }
    if (!isVendedorAllowedPath(pathname)) {
      return <AccessDenied userRole={userRole} redirectPath="/dashboard" />;
    }
  }

  // Si el usuario es diseñador o asesor_diseñador
  if (userRole === 'diseñador' || userRole === 'asesor_diseñador') {
    if (pathname === '/') {
      return <Navigate to="/dashboard" replace />;
    }
    if (!isDisenadorAllowedPath(pathname)) {
      return <AccessDenied userRole={userRole} redirectPath="/dashboard" />;
    }
  }

  // Si se especificaron roles permitidos, verificar
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    const redirectPath = userRole === 'aliado' ? '/panel-aliado' : '/dashboard';
    return <AccessDenied userRole={userRole} redirectPath={redirectPath} />;
  }

  return children;
};
